import { useMemo, useState } from 'react'

import type { Person } from '~/data/types'
import { theme } from '~/theme'
import { AgentCharacter, showsCharacter } from './AgentAvatar'

// A spatial directory: the org grouped by room the way the floor is, filtering
// live as you type. Doubles as "who's where" when idle and a jump-to-person
// search when you type. Clicking a row glides the camera to that person.

type Filter = 'all' | 'online' | 'agents'

function presenceDot(p: Person['presence']): string {
  if (p === 'online') return theme.online
  if (p === 'away') return theme.away
  if (p === 'offline') return theme.offline
  return theme.textDim
}

const UNGROUPED = 'Commons'

export function PeopleDirectory({
  people,
  seatRoomName,
  roomOrder,
  meId,
  focusedUserId,
  onSelect,
  onClose,
  compact,
}: {
  people: Person[]
  /** userId → the room the map seats them in (their group). */
  seatRoomName: Record<string, string>
  /** Room names in floor order, so groups read like the map top-to-bottom. */
  roomOrder: string[]
  meId: string | null
  focusedUserId: string | null
  onSelect: (userId: string) => void
  onClose: () => void
  compact?: boolean
}) {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const q = query.trim().toLowerCase()

  // Filter + group. A person matches on name / job / department / their room.
  const { groups, flatVisible, total, online } = useMemo(() => {
    const matches = (p: Person): boolean => {
      if (filter === 'online' && p.presence !== 'online') return false
      if (filter === 'agents' && !p.isAgent) return false
      if (!q) return true
      const room = seatRoomName[p.userId] ?? UNGROUPED
      return (
        p.name.toLowerCase().includes(q) ||
        (p.jobTitle ?? '').toLowerCase().includes(q) ||
        (p.department ?? '').toLowerCase().includes(q) ||
        room.toLowerCase().includes(q)
      )
    }
    const byRoom = new Map<string, Person[]>()
    let onlineCount = 0
    for (const p of people) {
      if (p.presence === 'online') onlineCount += 1
      if (!matches(p)) continue
      const room = seatRoomName[p.userId] ?? UNGROUPED
      const arr = byRoom.get(room) ?? []
      arr.push(p)
      byRoom.set(room, arr)
    }
    const order = (name: string): number => {
      if (name === UNGROUPED) return roomOrder.length + 1
      const i = roomOrder.indexOf(name)
      return i === -1 ? roomOrder.length : i
    }
    const sortedRooms = [...byRoom.keys()].sort((a, b) => order(a) - order(b))
    const grps = sortedRooms.map((room) => ({
      room,
      members: (byRoom.get(room) ?? []).sort((a, b) => {
        // Online first, then name — the useful reading order.
        if ((a.presence === 'online') !== (b.presence === 'online'))
          return a.presence === 'online' ? -1 : 1
        return a.name.localeCompare(b.name)
      }),
    }))
    const flat: string[] = []
    for (const g of grps) for (const m of g.members) flat.push(m.userId)
    return { groups: grps, flatVisible: flat, total: people.length, online: onlineCount }
  }, [people, seatRoomName, roomOrder, q, filter])

  const toggleGroup = (room: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(room)) next.delete(room)
      else next.add(room)
      return next
    })

  return (
    <aside style={compact ? sheetCompact : panel}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.cream, flex: 1 }}>
          Find people
        </div>
        <span style={{ fontSize: 10.5, color: theme.textDim }}>
          {online}/{total} online
        </span>
        <button onClick={onClose} style={closeBtn} aria-label="Close">×</button>
      </div>

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && flatVisible[0]) onSelect(flatVisible[0])
        }}
        placeholder="Search name, role, or department…"
        autoFocus
        style={search}
      />

      <div style={{ display: 'flex', gap: 6, margin: '8px 0 4px' }}>
        {(['all', 'online', 'agents'] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{ ...chip, ...(filter === f ? chipOn : null) }}
          >
            {f === 'all' ? 'All' : f === 'online' ? 'Online' : 'Agents'}
          </button>
        ))}
      </div>

      <div style={list}>
        {groups.length === 0 && (
          <div style={{ fontSize: 12, color: theme.textDim, padding: '14px 4px', textAlign: 'center' }}>
            No one matches “{query}”.
          </div>
        )}
        {groups.map((g) => {
          const isCollapsed = collapsed.has(g.room) && !q
          const onlineHere = g.members.filter((m) => m.presence === 'online').length
          return (
            <div key={g.room}>
              <button style={groupHead} onClick={() => toggleGroup(g.room)}>
                <span style={{ width: 12, color: theme.textDim }}>
                  {isCollapsed ? '▸' : '▾'}
                </span>
                <span style={{ flex: 1, textAlign: 'left', color: theme.cream, fontWeight: 700 }}>
                  {g.room}
                </span>
                <span style={{ fontSize: 10, color: theme.textDim }}>
                  {onlineHere}/{g.members.length}
                </span>
              </button>
              {!isCollapsed &&
                g.members.map((p) => (
                  <button
                    key={p.userId}
                    onClick={() => onSelect(p.userId)}
                    style={{
                      ...row,
                      ...(p.userId === focusedUserId ? rowActive : null),
                    }}
                    title={`Jump to ${p.name}`}
                  >
                    <span style={{ position: 'relative', flex: '0 0 auto' }}>
                      {showsCharacter(p) ? (
                        // Its character with its role's emblem where the dot would sit.
                        <AgentCharacter userId={p.userId} size={26} />
                      ) : (
                        <>
                          {p.picture || p.gravatar ? (
                            <img
                              src={p.picture || p.gravatar || ''}
                              alt=""
                              style={avatar}
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <span style={{ ...avatar, ...avatarFallback }}>
                              {p.name.slice(0, 1).toUpperCase()}
                            </span>
                          )}
                          <span
                            style={{
                              position: 'absolute',
                              right: -1,
                              bottom: -1,
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: presenceDot(p.presence),
                              border: `1.5px solid ${theme.panel}`,
                            }}
                          />
                        </>
                      )}
                    </span>
                    <span style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                      <span style={{ display: 'block', fontSize: 12.5, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.isAgent && !showsCharacter(p) ? '✦ ' : ''}
                        {p.name}
                        {p.userId === meId ? ' (you)' : ''}
                      </span>
                      {(p.jobTitle || p.department) && (
                        <span style={{ display: 'block', fontSize: 10.5, color: theme.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.jobTitle || p.department}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
            </div>
          )
        })}
      </div>
    </aside>
  )
}

const panel: React.CSSProperties = {
  width: 320,
  maxHeight: '74vh',
  display: 'flex',
  flexDirection: 'column',
  padding: 14,
  background: theme.panel,
  border: `1px solid ${theme.border}`,
  borderRadius: 12,
  color: theme.text,
}
const sheetCompact: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  maxHeight: '74vh',
  display: 'flex',
  flexDirection: 'column',
  padding: 14,
  paddingBottom: 'calc(14px + env(safe-area-inset-bottom))',
  background: theme.panel,
  borderTop: `1px solid ${theme.border}`,
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  color: theme.text,
}
const closeBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: theme.textDim,
  cursor: 'pointer',
  fontSize: 18,
  lineHeight: 1,
  padding: '0 4px',
}
const search: React.CSSProperties = {
  width: '100%',
  background: '#101218',
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  color: theme.text,
  fontSize: 12.5,
  padding: '8px 10px',
  outline: 'none',
}
const chip: React.CSSProperties = {
  background: 'none',
  border: `1px solid ${theme.border}`,
  color: theme.textDim,
  borderRadius: 999,
  fontSize: 11,
  padding: '3px 11px',
  cursor: 'pointer',
}
const chipOn: React.CSSProperties = {
  color: theme.cream,
  background: '#232633',
  borderColor: '#3a3f4d',
}
const list: React.CSSProperties = {
  marginTop: 6,
  overflowY: 'auto',
  flex: 1,
  minHeight: 0,
}
const groupHead: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  width: '100%',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 12,
  padding: '7px 2px 4px',
}
const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  width: '100%',
  background: 'none',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  padding: '5px 6px',
  marginBottom: 1,
}
const rowActive: React.CSSProperties = {
  background: '#232633',
}
const avatar: React.CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: '50%',
  objectFit: 'cover',
  display: 'block',
}
const avatarFallback: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#2a2e3a',
  color: theme.cream,
  fontSize: 12,
  fontWeight: 700,
}
